import { type FC, useState } from "react";

import { Container, Stack } from "@mui/material";
import { Form, Formik, type FormikHelpers, type FormikValues } from "formik";
import { type AnyObject, type ObjectSchema } from "yup";

import { stepLabels } from "../../Pages/CommitmentManager/consts";
import { ButtonsFooter } from "../ButtonsFooter/ButtonsFooter";
import { FullWidthStepper } from "./FullWidthStepper";
import { useWizardForm } from "./hooks";

export type WizardFormStepEvent = (stepIndex: number, action: "next" | "complete") => void;

type Props<T extends FormikValues & AnyObject> = {
  steps: FC[];
  onSubmit: (values: T) => Promise<void>;
  initialValues: T;
  validationSchema: ObjectSchema<object>[];
  onStepEvent?: WizardFormStepEvent;
};

export const WizardForm = <T extends FormikValues & AnyObject>({
  steps,
  onSubmit,
  initialValues,
  validationSchema,
  onStepEvent,
}: Props<T>) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { currentStep, next, back, StepComponent } = useWizardForm(steps);

  const handleSubmit = async (values: T, { setTouched, setSubmitting }: FormikHelpers<T>) => {
    if (currentStep !== steps.length - 1) {
      setTouched({});
      if (onStepEvent) onStepEvent(currentStep, "next");
      next();
    } else {
      setIsSubmitting(true);
      try {
        await onSubmit(values);
        if (onStepEvent) onStepEvent(currentStep, "complete");
      } catch (error) {
        setSubmitting(false);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema[currentStep]} onSubmit={handleSubmit}>
      <Form id="wizard-form" noValidate>
        <Container maxWidth="md" sx={{ my: 4, pb: 10 }} disableGutters>
          <FullWidthStepper steps={stepLabels} activeStep={currentStep} />
          <Stack mt={7}>
            <StepComponent />
          </Stack>
          <ButtonsFooter
            isLastStep={currentStep === steps.length - 1}
            onBackClicked={back}
            onNextClicked={next}
            allowNext={true}
            maxWidth="md"
            formId="wizard-form"
            finalStep="Create"
            hideBackButton={currentStep === 0}
            loading={isSubmitting}
          />
        </Container>
      </Form>
    </Formik>
  );
};
